/**
 * The seam between "a request was refused for want of a second factor" and
 * "ask the person for one".
 *
 * The gate protects whole route families (the vault, payroll, W-2) reached from
 * dozens of call sites, so the interceptor asks through this broker rather than
 * every caller catching `MFA_REAUTH_REQUIRED`. Why a broker, and why coalescing
 * is load-bearing (a TOTP code is single-use, so a second stacked prompt REFUSES
 * the code just typed into the first): `./stepUpBroker`.
 */
import { notifySecondFactorPresented } from './secondFactorPresented';
import { createStepUpBroker } from './stepUpBroker';

/** Resolves true when the person completed a challenge, false if they gave up. */
export type MfaChallengeHandler = () => Promise<boolean>;

const broker = createStepUpBroker<[]>('MfaStepUpBroker');

/**
 * Register the UI that can run a challenge. Returns an unregister function.
 *
 * Mounted by `MfaChallengeProvider`. If nothing registers — a surface that
 * never touches a gated route, or a non-React caller — `requestMfaStepUp`
 * resolves FALSE rather than hanging, and the original 401 surfaces normally.
 */
export function registerMfaChallengeHandler(fn: MfaChallengeHandler): () => void {
  return broker.register(fn);
}

/** True when a surface can actually run a challenge. */
export function canPromptForMfaStepUp(): boolean {
  return broker.canPrompt();
}

/** Ask for a step-up, coalescing concurrent asks into one prompt. Never throws. */
export function requestMfaStepUp(): Promise<boolean> {
  return broker.request().then((presented) => {
    // The session now holds a step-up: let the console carry it into its
    // Firebase session too (SOC 2 R73, `StaffFirebaseFactorSync`).
    if (presented) notifySecondFactorPresented();
    return presented;
  });
}

/** Test seam. Never call from application code. */
export function __resetMfaStepUpBrokerForTests(): void {
  broker.reset();
}
